import axios from 'axios';
import env from '../../../config';
import StarterKit from '@tiptap/starter-kit';
import { ArrowRightIcon } from '@chakra-ui/icons';
import { Button, Checkbox, Dialog, Flex, HStack, Portal, Stack, Text } from '@chakra-ui/react';
import { Control, RichTextEditor } from '../../../components/ui/rich-text-editor';
import { formatSlotSummary } from '@opanuj/shared/utils';
import { renderEmail } from '../../../utils/renderEmail';
import { reservationDelete } from '../../../operations/admin/reservationDelete';
import { reservationsAccept } from '../../../operations/admin/reservationAccept';
import { TimeRangeForm } from '../../../components/TimeRangeForm';
import { toaster } from '../../../components/ui/toaster';
import { useDatabase } from '../../../hooks/useDatabase';
import { useEditor } from '@tiptap/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useEmailTemplatesList } from '../../../hooks/useEmailTemplates';
import { useSettings } from '../../../hooks/useSettings';

type ReservationDecision = 'accept' | 'reject' | 'ignore' | '';

type ReservationReplyDialogProps = {
	isOpen: boolean;
	onClose: () => void;
	group: { id: string; items: Array<{ reservation: any; lesson: any; slot: any; client: any }> } | null;
};

export const syncReservationTemplateEditor = ({
	editor,
	nextHtml,
	lastSyncedTemplate,
}: {
	editor: ReturnType<typeof useEditor> | null;
	nextHtml: string;
	lastSyncedTemplate: { current: string };
}) => {
	if (!editor) {
		return;
	}

	const normalizedNextHtml = nextHtml || '<p>Treść wiadomości...</p>';
	if (lastSyncedTemplate.current === normalizedNextHtml) {
		return;
	}

	const currentHtml = editor.getHTML() || '';
	if (currentHtml === normalizedNextHtml) {
		lastSyncedTemplate.current = normalizedNextHtml;
		return;
	}

	lastSyncedTemplate.current = normalizedNextHtml;
	editor.commands.setContent(normalizedNextHtml, { emitUpdate: false });
};

const decisionOptions = [
	{ value: 'accept', label: 'Akceptuj' },
	{ value: 'reject', label: 'Odrzuć' },
	{ value: 'ignore', label: 'Ignoruj' },
] as const;

const ReservationReplyDialog = ({ isOpen, onClose, group }: ReservationReplyDialogProps) => {
	const database = useDatabase();
	const [decisionByReservation, setDecisionByReservation] = useState<Record<string, ReservationDecision>>({});
	const [dateByReservation, setDateByReservation] = useState<Record<string, { firstDay: string; lastDay: string }>>({});
	const [sendClientEmail, setSendClientEmail] = useState(true);
	const [submitPending, setSubmitPending] = useState(false);
	const [templates] = useEmailTemplatesList();
	const [settings] = useSettings();

	const clientName = group?.items[0]?.client?.name ?? group?.items[0]?.reservation?.email ?? 'klient';
	const relatedReservations = useMemo(() => group?.items ?? [], [group?.items]);

	const defaultTemplate = useMemo(
		() => templates.find((template) => template.id === 'reservation-approved-client') ?? templates[0] ?? null,
		[templates],
	);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		setDecisionByReservation(Object.fromEntries(
			(group?.items ?? []).map(({ reservation, slot }, index) => [
				getReservationItemKey(reservation, slot, index),
				'ignore' as const,
			]),
		));
		setDateByReservation({});
		setSendClientEmail(true);
	}, [group, isOpen]);

	const getReservationItemKey = (reservation: any, slot: any, index: number) => {
		const reservationKey = reservation?.id ?? `reservation-${index + 1}`;
		return slot?.id ? `${reservationKey}:${slot.id}` : reservationKey;
	};

	const updateDecision = (itemKey: string, value: ReservationDecision) => {
		setDecisionByReservation((current) => ({
			...current,
			[itemKey]: value,
		}));
	};

	const getReservationDateRange = (itemKey: string) => ({
		firstDay: dateByReservation[itemKey]?.firstDay ?? settings?.seasonSpan?.firstDay ?? '',
		lastDay: dateByReservation[itemKey]?.lastDay ?? settings?.seasonSpan?.lastDay ?? '',
	});

	const emailContext = useMemo(() => {
		const accepted = (relatedReservations ?? []).flatMap(({ reservation, lesson, slot }, index) => {
			const itemKey = getReservationItemKey(reservation, slot, index);
			if (decisionByReservation[itemKey] !== 'accept') {
				return [];
			}

			const summary = slot ? formatSlotSummary(slot) : lesson?.subject ?? lesson?.title ?? lesson?.name ?? 'Lekcja';
			return [{
				summary,
				studentName: lesson?.student?.name ?? '',
				firstDay: dateByReservation[itemKey]?.firstDay ?? settings?.seasonSpan?.firstDay ?? '',
				lastDay: dateByReservation[itemKey]?.lastDay ?? settings?.seasonSpan?.lastDay ?? '',
			}];
		});

		const rejected = (relatedReservations ?? []).flatMap(({ reservation, lesson, slot }, index) => {
			const itemKey = getReservationItemKey(reservation, slot, index);
			if (decisionByReservation[itemKey] !== 'reject') {
				return [];
			}
			return [{
				summary: slot ? formatSlotSummary(slot) : lesson?.subject ?? lesson?.title ?? lesson?.name ?? 'Lekcja',
				studentName: lesson?.student?.name ?? ''
			}];
		});

		const ignored = (relatedReservations ?? []).flatMap(({ reservation, lesson, slot }, index) => {
			const itemKey = getReservationItemKey(reservation, slot, index);
			if (decisionByReservation[itemKey] !== 'ignore') {
				return [];
			}
			return [{
				summary: slot ? formatSlotSummary(slot) : lesson?.subject ?? lesson?.title ?? lesson?.name ?? 'Lekcja',
				studentName: lesson?.student?.name ?? ''
			}];
		});

		return {
			clientName,
			client: group?.items[0]?.client ?? {},
			acceptedLessons: accepted,
			rejectedLessons: rejected,
			ignoredLessons: ignored,
			acceptedCount: accepted.length,
		};
	}, [clientName, dateByReservation, decisionByReservation, group, relatedReservations, settings?.seasonSpan?.firstDay, settings?.seasonSpan?.lastDay]);

	const renderedTemplateHtml = useMemo(() => {
		if (!defaultTemplate) {
			return '<p>Treść wiadomości...</p>';
		}

		return renderEmail(defaultTemplate.html ?? '<p>Treść wiadomości...</p>', emailContext);
	}, [defaultTemplate, emailContext]);

	const editor = useEditor({
		extensions: [StarterKit],
		content: renderedTemplateHtml,
		immediatelyRender: false,
		editorProps: {
			attributes: {
				style: 'min-height: 180px; padding: 0.75rem 1rem; outline: none;',
			},
		},
	});
	const lastSyncedTemplateRef = useRef('');

	useEffect(() => {
		if (!isOpen) {
			lastSyncedTemplateRef.current = '';
			return;
		}

		syncReservationTemplateEditor({
			editor,
			nextHtml: renderedTemplateHtml,
			lastSyncedTemplate: lastSyncedTemplateRef,
		});
	}, [editor, isOpen, renderedTemplateHtml]);

	const handleSubmit = async () => {
		if (!group || !database) {
			onClose();
			return;
		}

		setSubmitPending(true);
		try {
			const rejectedGroups = new Map<string, { reservationId?: string; reservationUid?: string; slotIds: string[] }>();
			for (const [index, { reservation, slot }] of relatedReservations.entries()) {
				const itemKey = getReservationItemKey(reservation, slot, index);
				if (decisionByReservation[itemKey] !== 'reject' || !slot?.id) {
					continue;
				}

				const reservationId = reservation?.id ?? reservation?.uid ?? '';
				const slotId = slot.id;
				const key = reservationId || reservation?.uid || slotId;
				const existing = rejectedGroups.get(key);
				if (existing) {
					existing.slotIds.push(slotId);
					rejectedGroups.set(key, existing);
				} else {
					rejectedGroups.set(key, {
						reservationId: reservationId || undefined,
						reservationUid: reservation?.uid,
						slotIds: [slotId],
					});
				}
			}

			await Promise.all(Array.from(rejectedGroups.values()).map(async ({ reservationId, reservationUid, slotIds }) => {
				if (!slotIds.length) {
					return;
				}
				await reservationDelete(database, {
					reservationId,
					reservationUid,
					slotIds: Array.from(new Set(slotIds)),
				});
			}));

			const acceptedGroups = new Map<string, {
				clientId?: string;
				clientUid?: string;
				slotId: string;
				firstDay: string;
				lastDay: string;
				reservationIds: string[];
			}>();

			for (const [index, { reservation, slot, client }] of relatedReservations.entries()) {
				const itemKey = getReservationItemKey(reservation, slot, index);
				if (decisionByReservation[itemKey] !== 'accept') {
					continue;
				}

				const entryDate = dateByReservation[itemKey] ?? getReservationDateRange(itemKey);
				if (!slot?.id || !entryDate.firstDay || !entryDate.lastDay) {
					continue;
				}

				const key = `${client?.id ?? reservation?.clientId ?? reservation?.uid ?? 'unknown'}:${slot.id}`;
				const existing = acceptedGroups.get(key);
				if (existing) {
					existing.reservationIds.push(reservation?.id ?? reservation?.uid ?? '');
					acceptedGroups.set(key, existing);
				} else {
					acceptedGroups.set(key, {
						clientId: client?.id,
						clientUid: reservation?.uid,
						slotId: slot.id,
						firstDay: entryDate.firstDay,
						lastDay: entryDate.lastDay,
						reservationIds: [reservation?.id ?? reservation?.uid ?? ''],
					});
				}
			}

			await Promise.all(Array.from(acceptedGroups.values()).map(async ({ clientId, clientUid, slotId, firstDay, lastDay, reservationIds }) => {
				await reservationsAccept(database, {
					slotId,
					clientId,
					clientUid,
					reservationIds: reservationIds.filter(Boolean),
					firstDay,
					lastDay,
				});
			}));

			const clientNotifications = Array.from(new Set(
				[...(group.items[0]?.client?.notifications?.emails ?? []), ...(group.items[0]?.client?.email ? [group.items[0].client.email] : [])]
					.filter(Boolean),
			));
			const emailWasSent = Boolean(sendClientEmail && clientNotifications.length && defaultTemplate);

			if (emailWasSent) {
				const finalEmailHtml = editor?.getHTML() || renderedTemplateHtml;
				await axios.post(env.endpoints.sendEmail, {
					to: clientNotifications,
					subject: defaultTemplate.subject || 'Potwierdzenie rezerwacji zajęć',
					html: finalEmailHtml,
					bcc: ['sekretariat@opanuj.to', 'rsoszynski121@gmail.com'],
				}, {
					headers: {
						'Content-Type': 'application/json',
					},
				});
			}

			toaster.create({
				title: 'Rezerwacja została obsłużona',
				description: emailWasSent ? 'Zapisano decyzję i wysłano wiadomość do klienta.' : 'Zapisano decyzję.',
				type: 'success',
				duration: 3000,
			});
		} catch (error) {
			console.error('Reservation response failed', error);
			toaster.create({
				title: 'Nie udało się obsłużyć rezerwacji',
				description: 'Spróbuj ponownie.',
				type: 'error',
				duration: 4000,
			});
		} finally {
			setSubmitPending(false);
			onClose();
		}
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={(event) => !event.open && onClose()} role="dialog">
			<Portal>
				<Dialog.Backdrop />
				<Dialog.Positioner>
					<Dialog.Content maxW="900px" w="95vw">
						<Dialog.Header>
							<Dialog.Title>Odpowiedź na rezerwację</Dialog.Title>
						</Dialog.Header>
						<Dialog.Body>
							<Stack gap={5}>
								<Text fontSize="sm">
									Wysyłasz odpowiedź do <strong>{clientName}</strong>.
								</Text>

								{relatedReservations.length > 0 && (
									<Stack gap={3} my={2}>
										<Text fontSize="sm" fontWeight="semibold" textTransform='uppercase' letterSpacing='wider' color="gray.500">Powiązane rezerwacje</Text>
										{relatedReservations.map(({ reservation, lesson, slot }, index) => {
											const itemKey = getReservationItemKey(reservation, slot, index);
											const itemDecision = decisionByReservation[itemKey] ?? '';
											const lessonName = slot ? formatSlotSummary(slot) : lesson?.subject ?? lesson?.title ?? lesson?.name ?? 'Lekcja';

											return (
												<Stack key={itemKey} gap={3}>
													<Flex justify="space-between" align="center" gap={4} wrap="wrap">
														<Text
															fontWeight="semibold"
															color={itemDecision === 'ignore' ? 'gray.500' : 'inherit'}
															opacity={itemDecision === 'ignore' ? 0.7 : 1}
															textDecoration={itemDecision === 'reject' ? 'line-through' : 'none'}
														>
															{lessonName}
														</Text>

														<Flex gap={4} wrap="wrap">
															{decisionOptions.map((option) => (
																<label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
																	<input
																		type="radio"
																		name={`reservation-decision-${itemKey}`}
																		checked={itemDecision === option.value}
																		onChange={() => updateDecision(itemKey, option.value)}
																	/>
																	<span>{option.label}</span>
																</label>
															))}
														</Flex>
													</Flex>

													{itemDecision === 'accept' && (
														<HStack pl="4">
															<ArrowRightIcon color="gray.400"/>
															<Text fontSize="sm" color="gray.700">początek i koniec zajęć</Text>
															<TimeRangeForm
																key={`${itemKey}-accept-range`}
																compact
																onSubmit={(values: { firstDay: string; lastDay: string }) => {
																	setDateByReservation((current) => ({
																		...current,
																		[itemKey]: values,
																	}));
																}}
																submitBtnText="Zastosuj"
																defaultValues={getReservationDateRange(itemKey)}
															/>
														</HStack>
													)}
												</Stack>
											);
										})}
									</Stack>
								)}

								<Checkbox.Root checked={sendClientEmail} onCheckedChange={(details) => setSendClientEmail(Boolean(details.checked))}>
									<Checkbox.HiddenInput />
									<Checkbox.Control />
									<Checkbox.Label>Wyślij email do klienta</Checkbox.Label>
								</Checkbox.Root>

								{sendClientEmail && (
									<Stack gap={2}>
										<Text fontSize="sm" fontWeight="semibold">Treść maila</Text>
										<RichTextEditor.Root editor={editor}>
											<RichTextEditor.Toolbar justify="space-between" align="center">
												<RichTextEditor.ControlGroup>
													<Control.Bold />
													<Control.Italic />
													<Control.Underline />
													<Control.Link />
												</RichTextEditor.ControlGroup>
												<RichTextEditor.ControlGroup>
													<Control.H2 />
													<Control.BulletList />
													<Control.OrderedList />
												</RichTextEditor.ControlGroup>
											</RichTextEditor.Toolbar>
											<RichTextEditor.Content />
										</RichTextEditor.Root>
									</Stack>
								)}
							</Stack>
						</Dialog.Body>
						<Dialog.Footer>
							<Button variant="ghost" onClick={onClose} disabled={submitPending}>Anuluj</Button>
							<Button colorPalette="cyan" onClick={handleSubmit} loading={submitPending} disabled={submitPending || !group}>{sendClientEmail ? 'Zatwierdź i wyślij' : 'Zatwierdź'}</Button>
						</Dialog.Footer>
					</Dialog.Content>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};

export default ReservationReplyDialog;
